import { useTranslation } from "react-i18next";
import { Languages } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/components/AuthProvider";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

/**
 * Compact language toggle (dropdown with 2 items).
 * Syncs the change to the backend so the choice persists across devices.
 */
export default function LanguageSwitcher({ compact = true, className = "" }) {
  const { i18n, t } = useTranslation();
  const { user, setUser } = useAuth();

  const change = async (lang) => {
    if (lang === i18n.language) return;
    await i18n.changeLanguage(lang);
    document.documentElement.setAttribute("lang", lang);
    if (user) {
      try {
        await api.patch("/users/me/language", { language: lang });
        if (setUser) setUser({ ...user, language: lang });
      } catch {
        // Backend sync is best-effort; localStorage already persisted the choice.
      }
    }
  };

  const label = i18n.language?.startsWith("en") ? "EN" : "ES";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className={`h-9 ${compact ? "w-9" : "px-3"} rounded-full grid place-items-center hover:bg-white/40 dark:hover:bg-white/10 transition text-sm font-medium ${className}`}
          aria-label={t("nav.language")}
          data-testid="language-switcher-trigger"
        >
          {compact ? <Languages className="h-4 w-4" /> : (
            <span className="inline-flex items-center gap-1.5">
              <Languages className="h-4 w-4" /> {label}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="glass-panel min-w-[140px]" data-testid="language-switcher-menu">
        <DropdownMenuItem
          onClick={() => change("es")}
          data-testid="language-option-es"
          className={i18n.language === "es" ? "bg-blue-500/10 text-blue-700 dark:text-blue-300 font-medium" : ""}
        >
          🇲🇽 Español
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => change("en")}
          data-testid="language-option-en"
          className={i18n.language === "en" ? "bg-blue-500/10 text-blue-700 dark:text-blue-300 font-medium" : ""}
        >
          🇺🇸 English
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
